import React, { memo, useState, useCallback, useEffect } from "react";
import { Link } from "react-router-dom";
import classNames from "classnames";
import styles from "./Header.module.scss";
import { useSelector } from "react-redux";
import { useAppDispatch } from "Redux/store";
import { selectPersonalGeneral } from "Redux/CompanyProps/slice";
import { getUnreadCount } from "Redux/Announcements/reducer";
import { selectUnreadSuccess } from "Redux/Announcements/slice";
import {
  selectNotificationsCount,
  selectUrgentNotificationsCount,
} from "Redux/Notifications/slice";
import { selectPermissions } from "Redux/Role/slice";
import { MODULES } from "Shared/Types/permissions";
import { read, readObj, write } from "Service/storage";
import GlobalSearch from "Components/UI/Inputs/InputSearch/GlobalSearch";
import ModalUserProfile from "Components/ModalUserProfile/ModalUserProfile";
import { Notification } from "Components/Notification/Notification";
import { Profile } from "Components/UI/Profile/Profile";
import { NotificationIcon, ThemeIcon } from "Assets";

const THEME_STORAGE_KEY = "theme";
const DEFAULT_THEME = "dark";

const Header: React.FC = () => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState<boolean | null>(
    null
  );

  const dispatch = useAppDispatch();
  const personalInfo = readObj("account");
  const companyInfo = useSelector(selectPersonalGeneral);
  const hasUrgentNotifications = useSelector(selectUrgentNotificationsCount);
  const notificationsCount = useSelector(selectNotificationsCount);
  const isUnreadCountFirstSuccess = useSelector(selectUnreadSuccess);
  const permissions = useSelector(selectPermissions);
  const notificationClasses = classNames(styles.notification, {
    [styles.notification_normal]: notificationsCount > 0,
    [styles.notification_urgent]: hasUrgentNotifications,
  });

  const hasNotificationAccess =
    permissions?.has_full_access &&
    permissions?.modules.has(MODULES.NOTIFICATIONS);

  const handleOpenNotifications = useCallback(() => {
    setIsNotificationOpen((prev) => !prev);
  }, []);
  const toggleTheme = useCallback(() => {
    const currentTheme =
      (read(THEME_STORAGE_KEY) as "light" | "dark") || DEFAULT_THEME;
    const newTheme = currentTheme === "dark" ? "light" : "dark";
    write(THEME_STORAGE_KEY, newTheme);
    document.documentElement.setAttribute("data-theme", newTheme);
  }, []);

  useEffect(() => {
    const storedTheme =
      (read(THEME_STORAGE_KEY) as "light" | "dark") || DEFAULT_THEME;
    document.documentElement.setAttribute("data-theme", storedTheme);
  }, []);
  const handleCloseNotifications = useCallback(() => {
    setIsNotificationOpen(false);
    setTimeout(() => setIsNotificationOpen(null), 600);
  }, []);

  const handleProfileClick = useCallback(() => {
    setIsProfileOpen(true);
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!isUnreadCountFirstSuccess) {
      dispatch(getUnreadCount());
    }
  }, [dispatch, isUnreadCountFirstSuccess]);

  useEffect(() => {
    if (!isUnreadCountFirstSuccess) {
      dispatch(getUnreadCount());
    }
  }, [dispatch, isUnreadCountFirstSuccess]);

  return (
    <div className={styles.content}>
      <div className={styles.contant__display}>
        <h3 className={styles.logo}>
          <Link to="/">
            Easy Fleet - {companyInfo?.account_name || "Loading..."}
          </Link>
        </h3>
        <GlobalSearch />

        <div className={styles.contant__display}>
          {hasNotificationAccess && (
            <div
              data-notification={notificationsCount}
              className={notificationClasses}
            >
              <NotificationIcon
                onClick={handleOpenNotifications}
                className={classNames(
                  styles.contant__display_blockIcon_icon,
                  styles.contant__display_blockIcon_icon_notification
                )}
              />
              <Notification
                isOpen={isNotificationOpen}
                onClose={handleCloseNotifications}
                className={styles.contant__notifications}
              />
            </div>
          )}

          <div onClick={toggleTheme} className={styles.theme}>
            <ThemeIcon width="23px" height="23px" />
          </div>

          <div
            onClick={handleProfileClick}
            onMouseEnter={handleMouseEnter}
            className={styles.contant__display_blockIcon}
          >
            {personalInfo?.profile_picture ? (
              <img
                src={personalInfo.profile_picture.file}
                alt="User Profile"
                className={styles.contant__display_blockIcon_icon}
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            ) : (
              <Profile
                className={classNames(
                  styles.contant__display_blockIcon_icon,
                  styles.contant__display_blockIcon_profileIcon
                )}
              />
            )}

            {isProfileOpen && <ModalUserProfile setOpen={setIsProfileOpen} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(Header);
